import { completionStatus } from '@codemirror/autocomplete';
import { EditorView, ViewPlugin, ViewUpdate } from '@codemirror/view';
import { type ComponentType, type ReactNode, createElement } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { IntlProvider } from 'react-intl';

import Borders from '@dynatrace/strato-design-tokens/borders';
import BoxShadows from '@dynatrace/strato-design-tokens/box-shadows';
import Colors from '@dynatrace/strato-design-tokens/colors';
import Spacings from '@dynatrace/strato-design-tokens/spacings';

import { SuggestionsInfoSection } from './SuggestionsInfoSection.js';

const SUGGESTIONS_INFO_ATTR = 'data-dql-suggestions-info';

function findTooltipElement(): HTMLElement | null {
  return document.querySelector<HTMLElement>('.cm-tooltip-autocomplete');
}

/**
 * Moves the `<completion-section>` to the top of the list when CodeMirror places it after the
 * first `<li>` on paginated pages. This happens because CodeMirror suppresses the section header
 * for the first item of non-first pages and the next item triggers it instead.
 */
function ensureCompletionSectionFirst(ul: HTMLUListElement): void {
  const sectionHeader = ul.querySelector('completion-section');
  if (sectionHeader && ul.firstElementChild !== sectionHeader) {
    ul.insertBefore(sectionHeader, ul.firstElementChild);
  }
}

// Strips the floating-box styling from the autocomplete tooltip container and
// reapplies it to the suggestion list (`ul`) so the suggestion list and the
// info section render as two visually independent floating boxes with a gap.
const suggestionsInfoSectionTheme = EditorView.baseTheme({
  '.cm-tooltip.cm-tooltip-autocomplete': {
    background: 'transparent !important',
    boxShadow: 'none !important',
    padding: '0 !important',
    borderRadius: '0 !important',
    display: 'flex !important',
    flexDirection: 'column',
    gap: Spacings.Size4,
    // Override the fixed `width: 500px` from the base editor config with a
    // responsive width, so the tooltip shrinks on narrow viewports. This is
    // what allows the container query in `SuggestionsInfoSection.css.ts` to
    // actually fire and reflow the info section to a column layout.
    width: `min(500px, calc(100vw - ${Spacings.Size16}))`,
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul': {
    background: Colors.Background.Surface.Default,
    borderRadius: Borders.Radius.Surface.Subdued,
    boxShadow: BoxShadows.Surface.Floating.Rest,
    padding: `${Spacings.Size4} !important`,
    flex: '0 1 auto',
    minHeight: 0,
  },
  // Pin the info section to the bottom regardless of DOM insertion order:
  // CodeMirror sometimes appends new children (e.g. the rebuilt `ul`) after our
  // section, leaving it visually above the suggestions. Setting a higher `order`
  // value forces flex to render it last while the `ul` keeps the default 0.
  [`.cm-tooltip.cm-tooltip-autocomplete > [${SUGGESTIONS_INFO_ATTR}]`]: {
    order: 1,
  },
});

const suggestionsInfoSectionPlugin = ViewPlugin.fromClass(
  class {
    private container: HTMLElement | null = null;
    private root: Root | null = null;
    private currentUl: HTMLUListElement | null = null;

    update(update: ViewUpdate) {
      const status = completionStatus(update.state);

      if (status === null) {
        // Defer the unmount so that root.unmount() is not called synchronously
        // inside a CodeMirror dispatch → plugin update stack. In React 19,
        // root.unmount() triggers a synchronous flush that conflicts with an
        // outer act() boundary in tests (and with React's batching in general).
        queueMicrotask(() => this.unmount());
        this.currentUl = null;
        return;
      }

      // Defer to ensure CodeMirror has finished creating the tooltip DOM.
      queueMicrotask(() => {
        const tooltipEl = findTooltipElement();
        const ul = tooltipEl?.querySelector<HTMLUListElement>('ul') ?? null;

        // Fix section-header position whenever the ul is (re)built, e.g. on pagination.
        if (ul && ul !== this.currentUl) {
          this.currentUl = ul;
          ensureCompletionSectionFirst(ul);
        }

        if (this.container?.isConnected) {
          return;
        }
        this.unmount();

        if (tooltipEl) {
          this.mount(tooltipEl);
        }
      });
    }

    private mount(tooltipEl: HTMLElement) {
      this.container = document.createElement('div');
      this.container.setAttribute(SUGGESTIONS_INFO_ATTR, '');
      tooltipEl.appendChild(this.container);

      this.root = createRoot(this.container);
      this.root.render(
        createElement(
          // react-intl ships @types/react@18 types. Its `contextType` references
          // the React 18 `Context<any>` which is missing the `$$typeof` symbol
          // required by React 19. The cast sidesteps the cross-version mismatch
          // while keeping the props shape explicit.
          IntlProvider as unknown as ComponentType<{
            locale: string;
            defaultLocale: string;
            messages: Record<string, string>;
            children?: ReactNode;
          }>,
          { locale: 'en', defaultLocale: 'en', messages: {} },
          createElement(SuggestionsInfoSection),
        ),
      );
    }

    private unmount() {
      this.root?.unmount();
      this.root = null;
      this.container?.remove();
      this.container = null;
    }

    destroy() {
      this.unmount();
    }
  },
);

export const suggestionsInfoSectionExtension = [
  suggestionsInfoSectionTheme,
  suggestionsInfoSectionPlugin,
];
